/** Aimed-weapon modules: `weapon.cannon` (a seat's gun with its own bearing —
 * fixed along the hull, or a turret B + left/right swings — firing pooled
 * shells from a muzzle along it while the primary button is held, with a
 * cooldown and a live-shell cap), `body.mount` (a kind drawn riding on its
 * host at the host's published aim: the turret over a hull) and
 * `body.ricochet` (a straight-flying shell that reflects off the arena's
 * edges and solid surfaces a bounded number of times, then is spent). The
 * cannon publishes the aim protocol of `body.treads` and lists its live
 * shells as `shots` (the live-shot protocol `shooterSlot` reads). */
import type { JsonObject } from '../../json.js';
import { id, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { pushVelocity } from '../physics.js';
import { overlaps, type EntityV1 } from '../world.js';
import { aimOf, bearingVector, centreOf, wrapAngle } from './treads.js';

export type CannonConfig = {
  /** The shell kind it fires (a `body.ricochet` body). */
  projectile: string;
  /** Shell speed (world units per second). */
  speed: number;
  cooldownTicks: number;
  /** Live shells a seat may have in the arena at once. */
  maxAlive: number;
  /** How far ahead of the hull's centre, along the aim, a shell appears. */
  muzzle: number;
  /** Swing rate (radians per second) of B + left/right; 0 fixes the gun along the hull. */
  aimRate: number;
  /** Where the aim points when a round starts: along the hull, or a seeded bearing. */
  startAim: 'hull' | 'random';
  /** The kind drawn as the turret (a `body.mount`), spawned with the hull. */
  turret?: string;
  /** What the solver's controller is drawn to attack (the rival hulls' tag). */
  targetTag: string;
};

/** Ticks the hull shows its `shoot` pose after a shot. */
const SHOOT_POSE_TICKS = 6;

function startAim(ctx: ModuleCtx<CannonConfig>, e: EntityV1): number {
  return ctx.config.startAim === 'random' && ctx.config.aimRate > 0 ? wrapAngle(ctx.random() * Math.PI * 2) : e.angle;
}

export const cannon = defineModule<CannonConfig>({
  id: 'weapon.cannon', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Aimed cannon: its bearing is fixed along the hull or a turret swung by B + left/right; while the primary button is held it fires a shell kind from a muzzle along the bearing, with a cooldown and a live-shell cap. Publishes its aim; optionally spawns a turret kind riding on the hull.',
  config: obj({ projectile: id(), speed: num(0.5, 60), cooldownTicks: int(1, 600), maxAlive: int(1, 16), muzzle: num(0, 4), aimRate: num(0, 20),
    startAim: oneOf('hull', 'random'), turret: id(), targetTag: id() }, ['turret']),
  requires: ['body.ricochet'], events: ['fire'], presentation: { fire: { cue: 'shot', vfx: { effect: 'flash', palette: 'slot' } } },
  proofs: ['fires-along-its-aim-while-held', 'cooldown', 'live-shell-cap', 'fixed-gun-follows-the-hull', 'b-swings-the-turret',
    'seeded-start-aim', 'round-start-reaims', 'spawns-its-turret', 'lists-live-shots'],
  animStates: () => ['shoot'],
  mapArchetypeRefs: (c, rename) => ({ ...c, projectile: rename(c.projectile), ...(c.turret !== undefined ? { turret: rename(c.turret) } : {}) }),
  check: (c, context) => [
    ...(context.archetypes[c.projectile]?.modules.includes('body.ricochet') ? [] : [`${c.projectile} must use body.ricochet`]),
    ...(c.turret !== undefined && !context.archetypes[c.turret]?.modules.includes('body.mount') ? [`${c.turret} must use body.mount`] : []),
    ...(c.startAim === 'random' && c.aimRate === 0 ? ['a gun fixed along the hull starts along it (startAim hull)'] : []),
  ],
  init(ctx, e) {
    const d = ctx.data(e);
    Object.assign(d, { aim: startAim(ctx, e), aimRate: ctx.config.aimRate, aiming: false, cooldown: 0, shots: [], pose: 0, fired: false });
    if (ctx.config.turret !== undefined) {
      const at = centreOf(e);
      d.turretId = ctx.spawn(ctx.config.turret, { x: at.x, y: at.y, params: { host: e.id } }).id;
    }
  },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      d.shots = (d.shots as string[]).filter(sid => ctx.world.entities[sid] !== undefined);
      d.aiming = false;
      if (e.status !== 'active') return;
      if (read(d, 'cooldown') > 0) d.cooldown = read(d, 'cooldown') - 1;
      const input = e.slot === null ? null : ctx.input(e.slot);
      if (c.aimRate > 0) {
        if (input && input.b && input.dx !== 0) { d.aim = wrapAngle(read(d, 'aim') + input.dx * c.aimRate * ctx.dt); d.aiming = true; }
      } else d.aim = e.angle;
      const shots = d.shots as string[];
      if (!input?.a || read(d, 'cooldown') > 0 || shots.length >= c.maxAlive) return;
      const at = centreOf(e), dir = bearingVector(read(d, 'aim'));
      const muzzle = { x: at.x + dir.x * c.muzzle, y: at.y + dir.y * c.muzzle };
      const shot = ctx.spawn(c.projectile, { x: muzzle.x, y: muzzle.y, params: { vx: dir.x * c.speed, vy: dir.y * c.speed } });
      // Spawned bottom-anchored: centre it on the muzzle.
      shot.y += shot.h / 2;
      shots.push(shot.id);
      d.cooldown = c.cooldownTicks;
      ctx.emit('fire', { x: muzzle.x, y: muzzle.y, slot: e.slot ?? 0 });
      d.pose = SHOOT_POSE_TICKS; d.fired = true;
    },
    present(ctx, e) {
      if (!e) return;
      const d = ctx.data(e);
      // A gun fixed along the hull bears where the hull now points.
      if (ctx.config.aimRate === 0) d.aim = e.angle;
      // A new round re-aims (after body.treads restored the hull's heading).
      if (ctx.signals('round-start').some(s => s.target === e.id)) { d.aim = startAim(ctx, e); d.cooldown = 0; d.pose = 0; }
      if (e.status === 'active' && read(d, 'pose') > 0) { ctx.setAnim(e, 'shoot', d.fired === true); d.pose = read(d, 'pose') - 1; }
      d.fired = false;
    },
  },
  // Every rival hull in play is a target to swing onto and shoot.
  solverTargets(ctx, e, player) {
    if (!e || e.id !== player.id || e.status !== 'active') return [];
    return ctx.entities({ tag: ctx.config.targetTag, status: 'active' })
      .filter(t => t.id !== e.id && !t.tags.includes('invulnerable'))
      .map(t => ({ ...centreOf(t), weight: 3, label: 'attack', press: true }));
  },
});

export type MountConfig = { follow: 'aim' | 'hull' };

/** The host a mounted entity rides (spawned with `params.host`). */
function hostOf(ctx: ModuleCtx<MountConfig>, e: EntityV1): EntityV1 | undefined {
  return ctx.entity(str(ctx.data(e), 'host'));
}

export const mount = defineModule<MountConfig>({
  id: 'body.mount', version: 1, attach: 'entity', role: 'movement',
  summary: 'Rides on its host (the entity that spawned it): centred on it, shown, frozen and hidden with it, turned to the host weapon\'s published aim or the host\'s own heading; removed with it.',
  config: obj({ follow: oneOf('aim', 'hull') }),
  requires: [], events: [], presentation: {}, proofs: ['centred-on-its-host', 'turns-with-the-host-aim', 'hidden-with-its-host', 'removed-with-its-host'],
  includeHidden: true,
  animStates: () => [],
  init(ctx, e, params: JsonObject) { ctx.data(e).host = typeof params.host === 'string' ? params.host : null; },
  systems: {
    // Present: after every stage that moved, re-seated or re-aimed the host.
    present(ctx, e) {
      if (!e) return;
      const host = hostOf(ctx, e);
      if (!host) { ctx.despawn(e, 'host-gone'); return; }
      const at = centreOf(host);
      e.x = at.x; e.y = at.y + e.h / 2; e.vx = host.vx; e.vy = host.vy;
      e.status = host.status;
      const aim = ctx.config.follow === 'aim' ? aimOf(host) : null;
      e.angle = aim ? aim.aim : host.angle;
    },
  },
});

export type RicochetConfig = {
  /** Ticks before an unspent shell burns out. */
  lifeTicks: number;
  /** Reflections it survives; the contact after the last one spends it. */
  bounces: number;
};

/** One axis of travel at its velocity plus `push` (that axis of the push
 * protocol's sum): move, and reflect off what it would enter. Returns false
 * when the shell is spent. */
function travel(ctx: ModuleCtx<RicochetConfig>, e: EntityV1, axis: 'x' | 'y', push: number): boolean {
  const d = ctx.data(e), v = axis === 'x' ? 'vx' : 'vy';
  const from = e[axis];
  e[axis] = from + (e[v] + push) * ctx.dt;
  const { w, h } = ctx.world.bounds;
  const out = axis === 'x' ? e.x - e.w / 2 < 0 || e.x + e.w / 2 > w : e.y - e.h < 0 || e.y > h;
  const wall = out || ctx.entities({ tag: 'surface', status: 'active' }).some(s => overlaps(ctx.world, e, s, 0.001));
  if (!wall) return true;
  if (read(d, 'left') <= 0) { ctx.despawn(e, 'spent'); return false; }
  d.left = read(d, 'left') - 1;
  e[axis] = from; e[v] = -e[v];
  ctx.emit('ricochet', { x: e.x, y: e.y - e.h / 2 });
  return true;
}

export const ricochet = defineModule<RicochetConfig>({
  id: 'body.ricochet', version: 1, attach: 'entity', role: 'movement',
  summary: 'Ricochet shell: flies straight at its spawn velocity, reflects off the arena edges and solid surfaces up to a bounce count (the next contact spends it), and burns out after a lifetime.',
  config: obj({ lifeTicks: int(1, 3600), bounces: int(0, 8) }),
  requires: [], events: ['ricochet'], presentation: { ricochet: { cue: 'bounce' } },
  proofs: ['straight-at-spawn-velocity', 'push-protocol', 'reflects-off-the-arena-edge', 'reflects-off-a-solid-surface', 'spent-after-its-bounces', 'burns-out'],
  animStates: () => [],
  init(ctx, e, params) {
    e.vx = typeof params.vx === 'number' ? params.vx : 0;
    e.vy = typeof params.vy === 'number' ? params.vy : 0;
    Object.assign(ctx.data(e), { age: 0, left: ctx.config.bounces });
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      d.age = read(d, 'age') + 1;
      if (read(d, 'age') > ctx.config.lifeTicks) { ctx.despawn(e, 'expired'); return; }
      const push = pushVelocity(ctx.signals('push'), e);
      if (travel(ctx, e, 'x', push.vx)) travel(ctx, e, 'y', push.vy);
    },
  },
});
